/**
 * UI Store (Zustand)
 *
 * Global state management for UI preferences and mobile state.
 * Persists mobile preferences to localStorage.
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createCloudSync } from '@shared/store/zustandCloudSync';
import { applyAccentColor } from '@/shared/utils/accentColor';

/**
 * Active panel types for mobile navigation
 */
export type ActivePanel = 'main' | 'filters' | 'details' | 'settings' | null;

/**
 * Theme mode (legacy - kept for compatibility)
 */
export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * Tool thinking display mode for Vibe Remote message rendering
 * - 'compact': All tool badges in a single row (current default)
 * - 'expanded': Tool badges grouped with thinking text between groups
 */
export type ToolThinkingDisplayMode = 'compact' | 'expanded';

/**
 * Whether to show inline diffs for Write/Edit tool operations
 * - true: Show green/red diff lines (default)
 * - false: Hide inline diff display
 */
export type ShowInlineDiffs = boolean;

/**
 * Theme class names used by CSS
 */
export type ThemeClass = 'theme-default' | 'theme-dark' | 'theme-light' | 'theme-dynamics';

/**
 * UI store state and actions
 */
interface UIStore {
  // Theme (CSS class-based)
  themeClass: ThemeClass;
  setThemeClass: (themeClass: ThemeClass) => void;

  // Default theme (persisted to PostgreSQL, applied on fresh sessions)
  defaultTheme: ThemeClass | null;
  setDefaultTheme: (theme: ThemeClass | null) => void;

  // Dynamics theme accent color (HSL)
  accentColor: { h: number; s: number; l: number };
  setAccentColor: (h: number, s: number, l: number) => void;

  // Theme mode (legacy - kept for compatibility)
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;

  // Mobile navigation
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  toggleMobileMenu: () => void;

  // Active panel (for mobile split-view navigation)
  activePanel: ActivePanel;
  setActivePanel: (panel: ActivePanel) => void;

  // Mobile filters
  mobileFiltersOpen: boolean;
  setMobileFiltersOpen: (open: boolean) => void;
  toggleMobileFilters: () => void;

  // Mobile modals
  activeMobileModal: string | null;
  openMobileModal: (modalId: string) => void;
  closeMobileModal: () => void;

  // Sidebar state (desktop)
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  // Sidebar expanded state (desktop - shows text labels below icons)
  sidebarExpanded: boolean;
  setSidebarExpanded: (expanded: boolean) => void;
  toggleSidebarExpanded: () => void;

  // Right sidebar state (desktop - collapsible panel)
  rightSidebarExpanded: boolean;
  setRightSidebarExpanded: (expanded: boolean) => void;
  toggleRightSidebar: () => void;

  // Focus mode (hides both sidebars for full-width center content)
  focusMode: boolean;
  setFocusMode: (enabled: boolean) => void;
  toggleFocusMode: () => void;

  // Mobile preferences
  preferences: {
    enablePullToRefresh: boolean;
    enableSwipeGestures: boolean;
    compactMode: boolean;
    showTutorial: boolean;
  };
  updatePreferences: (prefs: Partial<UIStore['preferences']>) => void;

  // Vibe Remote preferences
  vibeRemotePreferences: {
    toolThinkingDisplayMode: ToolThinkingDisplayMode;
    showInlineDiffs: boolean;
  };
  updateVibeRemotePreferences: (prefs: Partial<UIStore['vibeRemotePreferences']>) => void;

  // Toast notifications
  toasts: Array<{
    id: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
  }>;
  addToast: (
    message: string,
    type?: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Reset
  reset: () => void;
}

/**
 * Initial state
 */
const initialState = {
  themeClass: 'theme-dynamics' as ThemeClass,
  defaultTheme: null as ThemeClass | null, // User's preferred default theme (for fresh sessions)
  accentColor: { h: 210, s: 79, l: 46 }, // Default blue accent
  themeMode: 'dark' as ThemeMode,
  mobileMenuOpen: false,
  activePanel: null as ActivePanel,
  mobileFiltersOpen: false,
  activeMobileModal: null,
  sidebarCollapsed: false,
  sidebarExpanded: false,
  rightSidebarExpanded: false, // Collapsed by default
  focusMode: false, // Both sidebars visible by default
  preferences: {
    enablePullToRefresh: true,
    enableSwipeGestures: true,
    compactMode: false,
    showTutorial: true,
  },
  vibeRemotePreferences: {
    toolThinkingDisplayMode: 'compact' as ToolThinkingDisplayMode,
    showInlineDiffs: true, // Show green/red diff lines by default
  },
  toasts: [],
};

/**
 * Create UI store with localStorage persistence
 */
export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Theme class (CSS-based themes: theme-default, theme-dark, theme-light, theme-dynamics)
      setThemeClass: (themeClass) => {
        set({ themeClass });

        // Apply theme class to document
        if (typeof window !== 'undefined') {
          const root = document.documentElement;
          // Remove all theme classes
          root.classList.remove('theme-default', 'theme-dark', 'theme-light', 'theme-dynamics');
          // Add the new theme class
          root.classList.add(themeClass);

          // Apply accent color if Dynamics theme
          if (themeClass === 'theme-dynamics') {
            const { accentColor } = get();
            applyAccentColor(accentColor.h, accentColor.s, accentColor.l);
          }

          console.log(`[Theme] Applied theme: ${themeClass}`);
        }
      },

      // Default theme (saved to PostgreSQL, applied on fresh sessions/devices)
      setDefaultTheme: (theme) => {
        set({ defaultTheme: theme });
        console.log(`[Theme] Set default theme: ${theme}`);
      },

      // Accent color (for Dynamics theme)
      setAccentColor: (h, s, l) => {
        set({ accentColor: { h, s, l } });
        applyAccentColor(h, s, l);
      },

      // Theme mode (legacy)
      setThemeMode: (mode) => {
        set({ themeMode: mode });

        // Apply theme to document
        if (typeof window !== 'undefined') {
          if (mode === 'dark') {
            document.documentElement.classList.add('dark');
          } else if (mode === 'light') {
            document.documentElement.classList.remove('dark');
          } else {
            // System mode - check system preference
            const prefersDark = window.matchMedia(
              '(prefers-color-scheme: dark)'
            ).matches;
            if (prefersDark) {
              document.documentElement.classList.add('dark');
            } else {
              document.documentElement.classList.remove('dark');
            }
          }
        }
      },

      // Mobile menu
      setMobileMenuOpen: (open) => {
        set({ mobileMenuOpen: open });
      },

      toggleMobileMenu: () => {
        set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen }));
      },

      // Active panel
      setActivePanel: (panel) => {
        set({ activePanel: panel });
      },

      // Mobile filters
      setMobileFiltersOpen: (open) => {
        set({ mobileFiltersOpen: open });
      },

      toggleMobileFilters: () => {
        set((state) => ({ mobileFiltersOpen: !state.mobileFiltersOpen }));
      },

      // Mobile modals
      openMobileModal: (modalId) => {
        set({ activeMobileModal: modalId });
      },

      closeMobileModal: () => {
        set({ activeMobileModal: null });
      },

      // Sidebar
      setSidebarCollapsed: (collapsed) => {
        set({ sidebarCollapsed: collapsed });
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      // Sidebar expanded (shows text labels below icons)
      setSidebarExpanded: (expanded) => {
        set({ sidebarExpanded: expanded });
      },

      toggleSidebarExpanded: () => {
        set((state) => ({ sidebarExpanded: !state.sidebarExpanded }));
      },

      // Right sidebar (desktop collapsible panel)
      setRightSidebarExpanded: (expanded) => {
        set({ rightSidebarExpanded: expanded });
      },

      toggleRightSidebar: () => {
        set((state) => ({ rightSidebarExpanded: !state.rightSidebarExpanded }));
      },

      // Focus mode (hides both sidebars)
      setFocusMode: (enabled) => {
        set({ focusMode: enabled });
      },

      toggleFocusMode: () => {
        set((state) => ({ focusMode: !state.focusMode }));
      },

      // Preferences
      updatePreferences: (prefs) => {
        set((state) => ({
          preferences: { ...state.preferences, ...prefs },
        }));
      },

      // Vibe Remote preferences
      updateVibeRemotePreferences: (prefs) => {
        set((state) => ({
          vibeRemotePreferences: { ...state.vibeRemotePreferences, ...prefs },
        }));
      },

      // Toasts
      addToast: (message, type = 'info', duration = 3000) => {
        const id = `toast-${Date.now()}-${Math.random()}`;
        const toast = { id, message, type, duration };

        set((state) => ({
          toasts: [...state.toasts, toast],
        }));

        // Auto-remove toast after duration
        if (duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, duration);
        }
      },

      removeToast: (id) => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      },

      clearToasts: () => {
        set({ toasts: [] });
      },

      // Reset
      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'dms-ui-storage', // localStorage key
      partialize: (state) => ({
        // Only persist these fields
        themeClass: state.themeClass,
        defaultTheme: state.defaultTheme,
        accentColor: state.accentColor,
        themeMode: state.themeMode,
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarExpanded: state.sidebarExpanded,
        rightSidebarExpanded: state.rightSidebarExpanded,
        focusMode: state.focusMode,
        preferences: state.preferences,
        vibeRemotePreferences: state.vibeRemotePreferences,
      }),
      // Apply theme on rehydration (page load)
      onRehydrateStorage: () => (state) => {
        if (state?.themeClass && typeof window !== 'undefined') {
          const root = document.documentElement;
          root.classList.remove('theme-default', 'theme-dark', 'theme-light', 'theme-dynamics');
          root.classList.add(state.themeClass);

          // Apply accent color if Dynamics theme
          if (state.themeClass === 'theme-dynamics' && state.accentColor) {
            applyAccentColor(state.accentColor.h, state.accentColor.s, state.accentColor.l);
          }

          console.log(`[Theme] Restored theme on load: ${state.themeClass}`);
        }
      },
    }
  )
);

/**
 * Selectors for common UI state
 */
export const uiSelectors = {
  /**
   * Check if any mobile panel is open
   */
  isMobilePanelOpen: (state: UIStore): boolean => {
    return state.mobileMenuOpen || state.mobileFiltersOpen || !!state.activeMobileModal;
  },

  /**
   * Check if UI is in mobile mode (not viewport detection, just UI state)
   */
  isMobileUIActive: (state: UIStore): boolean => {
    return !!state.activePanel || state.mobileMenuOpen;
  },
};

// ==================== Cloud Sync ====================

// Cloud sync for UI preferences
const uiCloudSync = createCloudSync<{
  themeClass: ThemeClass;
  defaultTheme: ThemeClass | null;
  accentColor: { h: number; s: number; l: number };
  themeMode: ThemeMode;
  sidebarCollapsed: boolean;
  sidebarExpanded: boolean;
  rightSidebarExpanded: boolean;
  focusMode: boolean;
  preferences: UIStore['preferences'];
  vibeRemotePreferences: UIStore['vibeRemotePreferences'];
}>({
  key: 'ui-preferences',
  namespace: 'ui',
  debounceMs: 500,
});

// Module-level subscription removed - use useUIStoreCloudSync() hook instead
// to ensure proper cleanup and React lifecycle management

/**
 * Hook to sync UI store changes to cloud (PostgreSQL)
 * Must be called from a component during initialization
 * Handles cleanup on unmount
 */
export function useUIStoreCloudSync() {
  useEffect(() => {
    // Subscribe to preference changes
    const unsubscribe = useUIStore.subscribe((state) => {
      uiCloudSync.syncToCloud({
        themeClass: state.themeClass,
        defaultTheme: state.defaultTheme,
        accentColor: state.accentColor,
        themeMode: state.themeMode,
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarExpanded: state.sidebarExpanded,
        rightSidebarExpanded: state.rightSidebarExpanded,
        focusMode: state.focusMode,
        preferences: state.preferences,
        vibeRemotePreferences: state.vibeRemotePreferences,
      });
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);
}

/**
 * Load from cloud on app initialization
 * Call this during app startup to restore preferences from PostgreSQL
 */
export async function initUIFromCloud(): Promise<void> {
  const cloudState = await uiCloudSync.loadFromCloud();
  if (cloudState) {
    useUIStore.setState(cloudState);

    // Apply theme class immediately after loading
    if (cloudState.themeClass && typeof window !== 'undefined') {
      const root = document.documentElement;
      root.classList.remove('theme-default', 'theme-dark', 'theme-light', 'theme-dynamics');
      root.classList.add(cloudState.themeClass);

      // Apply accent color if Dynamics theme
      if (cloudState.themeClass === 'theme-dynamics' && cloudState.accentColor) {
        applyAccentColor(cloudState.accentColor.h, cloudState.accentColor.s, cloudState.accentColor.l);
      }
    }
  }
}
